import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent
} from 'react';
import {
  CmhcIcon,
  CmhcIconSize,
  MRTDataTable,
  DropdownWithSearch,
  Button,
  ButtonVariant,
  Tag,
  TagType,
  TagVariant,
  SplitPane,
  ToastNotification,
  ToastNotificationVariant,
  TabFields
} from '@cmhc-schl/hearth-ui';
import lodash from 'lodash';
import { Box, ClickAwayListener } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { MRT_Localization, MRT_Row, MRT_SortingState } from 'material-react-table';
import { useTranslation } from 'react-i18next';
import { getLoanInventoryTableData } from './api/RequestOverviewService';
import ErrorMessage, { SpinnerElement } from './components/ErrorMessage';
import { createLoansInventoryColumns } from './columns/loansInventoryTableColumns';
import { HbtMasterPoolLoansInventoryProps, LoanInventoryTableFields } from './types';
import styles from './styles.module.scss';
import { FilterOption } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import { errorOptionsGrouped } from './utils/errorOptionsGrouped';
import { errorSeverityMapping } from './utils/errorSeverityMapping';
import { cmhcAccountNumberOptionsGrouped } from './utils/cmhcAccountNumberOptionsGrouped';
import { statusOptionsGrouped } from './utils/statusOptionsGrouped';
import { useLoansErrors } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/hooks/useLoanErrors';
import { ActionDropdownWithListItems } from 'Feature/V2/HbtPortfolioManagement/shared/components/ActionDropdownWithListItems';
import { applyFilters } from './utils/applyFilters';
import RenderRowActions from './components/RenderRowActions/RenderRowActions';
import RightPaneContent from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent';
import ForceStatusFlyout from '../HbtMasterPool/component/ForceStatusFlyout';
import ActionDetailsButtons from './components/ActionDetailsButtons/ActionDetailsButtons';
import { useMRTCustomTranslation } from 'Feature/V2/Shared_Portfolio/Hooks/useMRTCustomTransaltion';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { useLocation } from 'react-router-dom';
import HbtMasterPoolLoansInventoryDetail from '../HbtMasterPoolLoansInventoryDetail/HbtMasterPoolLoansInventoryDetail';
import HistoricalDataFlyout from './components/HistoricalDataFlyout/HistoricalDataFlyout';
import { config } from '../../../../../config';
import { MasterPoolLoanStatus } from '../HbtMasterPoolTable/utils/LoanStatusMapping';
import { dropdownIdsApprovedStatus, dropdownIdsWithdrawnStatus } from '../../constants';

const origin = config.app.internalOriginUrl;
const MAX_VISIBLE_FILTERS = 10;
const fetchSize = origin?.includes('localhost') ? 100 : 20000;
const isRowActionsMenuVisible = false;

const HbtMasterPoolLoansInventoryTable = ({
  masterPoolAccountNumber,
  handleViewLoanDetailsActionRowClick,
  handleMutipleLoanDetailsActionClick,
  setMasterPoolNotification,
  setNotificationBannerConfig,
  setIsForceStatusTriggered
}: HbtMasterPoolLoansInventoryProps) => {
  const [loanInventoryError, setLoanInventoryError] = useState<string>('');
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRowsData, setSelectedRowsData] = useState<LoanInventoryTableFields[]>([]);
  const authenticationContext = useAuthenticationContext();
  const { t } = useTranslation('HbtMasterPool');
  const columns = useMemo(() => createLoansInventoryColumns(t, i18n.language), [t, i18n.language]);
  const loansUpdateStatusUrl: string =
    config.validationReviewInventoryApi.urls.loanInventoryTabDetails + `/loans/status-update`;
  const loansUpdateRemarkUrl: string =
    config.validationReviewInventoryApi.urls.loanInventoryTabDetails + `/loans/remarks-update`;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [showRightPane, setShowRightPane] = useState(false);
  const handleCloseFlyout = () => setShowRightPane((prev) => !prev);
  const [selectedErrorCodes, setSelectedErrorCodes] = useState<Record<string, FilterOption[]>>({});
  const [selectedStatuses, setSelectedStatuses] = useState<FilterOption[]>([]);
  const [selectedCmhcAccountNumber, setSelectedCmhcAccountNumber] = useState<FilterOption[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([]);
  const [isSelectedFiltersMoreMenuOpen, setSelectedFiltersMoreMenuOpen] = useState(false);
  const [openForceStatusFlyout, setOpenForceStatusFlyout] = React.useState<boolean>(false);
  const [openAddRemarksFlyout, setOpenAddRemarksFlyout] = React.useState<boolean>(false);
  const [openHistoricalFlyout, setOpenHistoricalFlyout] = React.useState<boolean>(false);
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });
  const { data: errorMeta } = useLoansErrors();
  const errorMapping = errorSeverityMapping(t);
  const translations = {
    searchList: t('Pi-Master-Pool-SearchList'),
    selectAll: t('Pi-Master-Pool-Select-All'),
    clearSection: t('Pi-Master-Pool-Clear-Selection'),
    selected: t('Pi-Master-Pool-Selected'),
    noInfo: t('Pi-Master-Pool-NoInfo')
  };
  const isViewLoanDetailsButtonVisible = selectedRowsData.length > 0;
  const location = useLocation();
  const { loanId } = getSearchParams(location.search);
  let dropDownOptionsForUpdateRemarks = [
    {
      ids: dropdownIdsWithdrawnStatus,
      groupName: ''
    }
  ];
  const isApprovedStatus =
    selectedRowsData.length > 0 && selectedRowsData[0].loanStatus === MasterPoolLoanStatus.Approved;
  if (isApprovedStatus) {
    dropDownOptionsForUpdateRemarks = [
      {
        ids: dropdownIdsApprovedStatus,
        groupName: ''
      }
    ];
  }

  const handleRowClick = (
    row: MRT_Row<LoanInventoryTableFields>,
    _: number,
    evt: React.MouseEvent
  ) => {
    const isCheckBoxCol = Boolean((evt.target as HTMLElement).closest('.MuiCheckbox-root'));
    if (!isCheckBoxCol) {
      const rowId = row.id;
      const newSelection = { [rowId]: true };

      setRowSelection(newSelection);
    }
  };

  const fetchInventoryData = async ({ pageParam = 0 }) => {
    const offset = pageParam;
    let sortField = sorting[0]?.id ?? '';

    if (sortField === 'approvedLender') {
      sortField = i18n.language === 'en' ? 'approvedLenderEnglishName' : 'approvedLenderFrenchName';
    }
    const sortOrder = !!sorting[0]?.desc;

    const queryParams = `?offset=${offset}&size=${fetchSize}${
      sortField ? `&sortField=${sortField}&sortOrder=${sortOrder ? 'desc' : 'asc'}` : ''
    }`;

    const data = await getLoanInventoryTableData({
      authenticationContext,
      masterPoolAccountNumber,
      queryParams
    });
    return data;
  };

  const {
    data: loanInventoryData,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    isSuccess
  } = useInfiniteQuery({
    queryKey: ['masterpool-inventory-loans', sorting],
    queryFn: fetchInventoryData,
    initialData: {
      pages: [[] as LoanInventoryTableFields[]],
      pageParams: [0]
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      const hasMore = lastPage.length === fetchSize;
      return hasMore ? totalFetched : undefined;
    }
  });

  const throttleRowFocus = useMemo(
    () =>
      lodash.throttle((row) => {
        const rowId = row.id;
        const newSelectedRow = { [rowId]: true };
        setRowSelection(newSelectedRow);
      }, 150),
    []
  );

  const flatData = useMemo(() => {
    setRowSelection({});
    return loanInventoryData?.pages.flat() ?? [];
  }, [loanInventoryData]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage]
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    if (isError) {
      setLoanInventoryError('api_error');
    } else if (!isFetching && isSuccess && flatData.length > 0) {
      setLoanInventoryError('');
    } else if (!isFetching && isSuccess && flatData.length === 0) {
      setLoanInventoryError('no_data_error');
    }
  }, [isSuccess, flatData, loanInventoryError, isError, isFetching]);

  useEffect(() => {
    const selectedRows = filteredData.filter((row) => rowSelection[row.cmhcAccountNumber]);
    setSelectedRowsData(selectedRows);
  }, [rowSelection, flatData]);

  useEffect(() => {
    const updatedSelectedErrorCodes = { ...selectedErrorCodes };
    const orderedSelectedErrorCodes: Record<string, FilterOption[]> = {};

    Object.entries(errorMapping)
      .map(([severity]) => severity)
      .forEach((item) => {
        if (updatedSelectedErrorCodes[item]) {
          orderedSelectedErrorCodes[item] = updatedSelectedErrorCodes[item].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true })
          );
        }
      });

    setSelectedFilters([
      ...selectedStatuses,
      ...selectedCmhcAccountNumber,
      ...Object.values(orderedSelectedErrorCodes).flat()
    ]);
  }, [selectedStatuses, selectedCmhcAccountNumber, selectedErrorCodes]);

  const errorGrouped = useMemo(
    () => errorMeta && errorOptionsGrouped(flatData, errorMeta, t),
    [flatData, errorMeta]
  );

  const statusOptions = useMemo(() => statusOptionsGrouped(flatData, t), [flatData]);

  const cmhcAccountNumberOptions = useMemo(
    () => cmhcAccountNumberOptionsGrouped(flatData, t),
    [flatData]
  );
  const filteredData = useMemo(() => {
    if (selectedRowsData.length > 0 && selectedFilters.length > 0) {
      setRowSelection({});
      setSelectedRowsData([]);
      if (showRightPane) {
        handleCloseFlyout();
      }
    }
    return applyFilters(flatData, selectedErrorCodes, selectedStatuses, selectedCmhcAccountNumber);
  }, [flatData, selectedErrorCodes, selectedStatuses, selectedCmhcAccountNumber]);

  useEffect(() => {
    setRowSelection({});
    setSelectedRowsData([]);
    if (showRightPane) {
      handleCloseFlyout();
    }
  }, [selectedStatuses, selectedCmhcAccountNumber, selectedErrorCodes]);

  const handleRemoveFilter = (filter: FilterOption) => {
    if (selectedStatuses.some((s) => s.value === filter.value)) {
      setSelectedStatuses((prev) => prev.filter((s) => s.value !== filter.value));
    }
    if (selectedCmhcAccountNumber.some((p) => p.value === filter.value)) {
      setSelectedCmhcAccountNumber((prev) => prev.filter((p) => p.value !== filter.value));
    }

    const [severity] = filter.value.split(':');
    setSelectedErrorCodes((prev) => ({
      ...prev,
      [severity]: (prev[severity] || []).filter((error) => error.value !== filter.value)
    }));
  };

  const handleClearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedCmhcAccountNumber([]);
    setSelectedErrorCodes({});
  };
  const toggleRightPane = () => {
    setShowRightPane((prev) => !prev);
  };

  const selectedFiltersMoreMenuOptions = useMemo(() => {
    return selectedFilters.slice(MAX_VISIBLE_FILTERS).map((filter, index) => ({
      label: filter.label,
      value: `${index}`,
      icon: <CmhcIcon size={CmhcIconSize.SMALL} type="icon_document" />,
      onClick: () => handleRemoveFilter(filter),
      chipLabel: filter.chipLabel
    }));
  }, [selectedFilters]);

  const showTableData = !isLoading && !isFetching && isSuccess && loanInventoryError.length < 1;
  const showErrorComponent = !isLoading && !isFetching && loanInventoryError.length > 0;
  const mrtCustomTranslation = useMRTCustomTranslation(filteredData.length);

  useEffect(() => {
    if (
      loanId.length > 0 &&
      filteredData.length > 0 &&
      sessionStorage.getItem('searchLoanDetail')
    ) {
      const selectedRowsData = filteredData.filter((row) => row.loanId === loanId);
      const tabs = selectedRowsData.map((row) => {
        const tabField: TabFields = {
          label: { value: row.cmhcAccountNumber },
          content: (
            <HbtMasterPoolLoansInventoryDetail
              loanReferenceNumber={row.lenderReferenceNumber}
              loanPortfolioEpipProcessingStatus={row.loanStatus}
              cmhcAccountNumber={row.cmhcAccountNumber}
              loanInventoryData={row}
              setNotificationBannerConfig={setNotificationBannerConfig}
              setMasterPoolNotification={setMasterPoolNotification}
              setIsForceStatusTriggered={setIsForceStatusTriggered}
            />
          )
        };
        return tabField;
      });
      handleMutipleLoanDetailsActionClick(tabs);
    }
  }, [isSuccess, loanId, filteredData]);

  const handleHistoricalDataFlyout = () => {
    setOpenHistoricalFlyout(false);
    setSelectedRowsData([]);
    setRowSelection({});
  };

  const callToaster = (icon: string, message: string, desc = '') => {
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };
  return (
    <>
      <ForceStatusFlyout
        openFlyout={openForceStatusFlyout}
        toggleDrawer={setOpenForceStatusFlyout}
        headerText={t('Update-Loan-Status-MasterPool-Loans-Inventory-Header-Text')}
        loans={selectedRowsData.map((rows) => rows.cmhcAccountNumber).join('\n')}
        masterPoolCmhcAccountNumber={masterPoolAccountNumber}
        isFromLoansInventory={true}
        radioButtonOptions={[
          { label: t('Approved'), value: '06' },
          { label: t('Withdrawn'), value: '05' }
        ]}
        requestUrl={loansUpdateStatusUrl}
        typeOfFlyout={'changeStatus'}
        setNotificationBannerConfig={setNotificationBannerConfig}
        setMasterPoolNotification={setMasterPoolNotification}
        textFieldButtonText={t('Add-CMHC-Account-Numbers')}
        setIsForceStatusTriggered={setIsForceStatusTriggered}
        callToaster={callToaster}
        errorModalDesc={t('Add-Loans-Error-Modal-Body-Text-Force-Status')}
      />
      <ForceStatusFlyout
        callToaster={callToaster}
        openFlyout={openAddRemarksFlyout}
        toggleDrawer={setOpenAddRemarksFlyout}
        loans={selectedRowsData.map((rows) => rows.cmhcAccountNumber).join('\n')}
        loansData={selectedRowsData}
        masterPoolCmhcAccountNumber={masterPoolAccountNumber}
        remarksDropDownHeader={dropDownOptionsForUpdateRemarks}
        isFromLoansInventory={true}
        radioButtonOptions={[
          { label: t('Approved'), value: '06' },
          { label: t('Withdrawn'), value: '05' }
        ]}
        requestUrl={loansUpdateRemarkUrl}
        typeOfFlyout={'updateRemark'}
        flyoutTitle={t('Add-Remarks-Flyout-Heading')}
        setNotificationBannerConfig={setNotificationBannerConfig}
        setMasterPoolNotification={setMasterPoolNotification}
        setIsForceStatusTriggered={setIsForceStatusTriggered}
        headerText={t('Add-Remarks-Flyout-Heading-Desc')}
        errorModalHeader={t('Error-Modal-Header')}
        textFieldButtonText={t('Add-CMHC-Account-Numbers')}
      />

      <HistoricalDataFlyout
        openFlyout={openHistoricalFlyout}
        toggleFlyout={handleHistoricalDataFlyout}
        selectedRowsData={selectedRowsData}
      />
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>
      {isFetching && (
        <Box className={styles.spinnerElementWrapper}>
          <SpinnerElement />
        </Box>
      )}
      {showErrorComponent && (
        <Box
          sx={{
            padding: '32px;'
          }}
        >
          <ErrorMessage
            errorType={loanInventoryError}
            content={t('Empty-Data-Error-Content-Master-Pool')}
          />
        </Box>
      )}
      {showTableData && (
        <>
          <ActionDetailsButtons
            isViewLoanDetailsButtonVisible={isViewLoanDetailsButtonVisible}
            selectedRowsData={selectedRowsData}
            handleMutipleLoanDetailsActionClick={handleMutipleLoanDetailsActionClick}
            toggleRightPane={toggleRightPane}
            setOpenForceStatusFlyout={setOpenForceStatusFlyout}
            setOpenAddRemarksFlyout={setOpenAddRemarksFlyout}
            openHistoricalFlyout={() => setOpenHistoricalFlyout(true)}
          />
          <Box className={styles.filterDropdownWrapper}>
            <Box className={styles.quickFiltersTitle}>{t('Pi-Master-Pool-Quick-Filters')} </Box>
            <DropdownWithSearch
              label={t('CMHC-Account-Number')}
              options={cmhcAccountNumberOptions}
              selected={selectedCmhcAccountNumber}
              setSelected={setSelectedCmhcAccountNumber}
              translations={translations}
            />
            <DropdownWithSearch
              label={t('Pi-Master-Pool-Loan-Status')}
              options={statusOptions}
              selected={selectedStatuses}
              setSelected={setSelectedStatuses}
              translations={translations}
            />
            {errorGrouped &&
              Object.entries(errorGrouped).map(([severity, options], index) => (
                <DropdownWithSearch
                  key={index}
                  label={errorMapping[severity as keyof typeof errorMapping]}
                  options={options as FilterOption[]}
                  translations={translations}
                  selected={selectedErrorCodes[severity] ?? []}
                  setSelected={(newSelection: any) =>
                    setSelectedErrorCodes((prev) => ({
                      ...prev,
                      [severity]: newSelection
                    }))
                  }
                />
              ))}
          </Box>
          {selectedFilters.length > 0 && (
            <Box className={styles.filterChips}>
              {selectedFilters.slice(0, MAX_VISIBLE_FILTERS).map((filter) => (
                <Tag
                  key={filter.value}
                  label={filter.chipLabel || ''}
                  tagType={TagType.TAG}
                  tagVariant={TagVariant.INTERACTIVE}
                  onDelete={() => handleRemoveFilter(filter)}
                  showStatusIndicator={false}
                />
              ))}
              {selectedFilters.length > MAX_VISIBLE_FILTERS && (
                <ClickAwayListener onClickAway={() => setSelectedFiltersMoreMenuOpen(false)}>
                  <Box>
                    <ActionDropdownWithListItems
                      className={styles.selectedFiltersMoreMenu}
                      open={isSelectedFiltersMoreMenuOpen}
                      onToggle={setSelectedFiltersMoreMenuOpen}
                      options={selectedFiltersMoreMenuOptions}
                      trigger={
                        <Button
                          variant={ButtonVariant.TERTIARY}
                          name="more-options-dropdown-button"
                          text={{
                            value: `+ ${selectedFilters.length - MAX_VISIBLE_FILTERS} ${t(
                              'Pi-Master-Pool-Quick-Filters-More'
                            )}`
                          }}
                        />
                      }
                    />
                  </Box>
                </ClickAwayListener>
              )}
              <Button
                onClick={handleClearAllFilters}
                variant={ButtonVariant.TERTIARY}
                name="Clear Filters"
                text={{
                  value: t('Pi-Master-Pool-Clear-Filters')
                }}
                leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_clear_circle" />}
              >
                {t('Pi-Master-Pool-Clear-Filters')}
              </Button>
            </Box>
          )}

          <SplitPane
            leftPaneMinWidth={500}
            rightPaneMinWidth={374}
            showRightPane={showRightPane}
            leftPane={
              <div className={styles.dataTableContainer}>
                <MRTDataTable<LoanInventoryTableFields>
                  data={filteredData || []}
                  columns={columns}
                  getRowId={(originalRow) => originalRow.cmhcAccountNumber.toString()}
                  enableRowVirtualization={true}
                  enableColumnVirtualization={true}
                  enableRowSelection
                  localization={mrtCustomTranslation}
                  memoMode="cells"
                  columnResizeMode="onChange"
                  muiTableBodyCellProps={({ row }) => ({
                    tabIndex: 0,
                    onFocus: (e) => {
                      if ((e.target as HTMLElement).matches(':focus-visible') && showRightPane) {
                        throttleRowFocus(row);
                      }
                    }
                  })}
                  enableRowActions={isRowActionsMenuVisible}
                  enableSorting={true}
                  isLoading={isLoading}
                  initialState={{
                    columnPinning: {
                      left: isRowActionsMenuVisible
                        ? ['mrt-row-actions', 'mrt-row-select', 'cmhcAccountNumber']
                        : ['mrt-row-select', 'cmhcAccountNumber']
                    }
                  }}
                  enableBottomToolbar={false}
                  enablePagination={false}
                  muiTableBodyRowProps={({ row }) => ({
                    tabIndex: 0,
                    onClick: (e) => handleRowClick(row, 0, e),
                    onKeyDown: (e) => {
                      if (e.key === 'Enter') {
                        row.toggleSelected();
                      }
                    }
                  })}
                  onRowClick={handleRowClick}
                  onRowSelectionChange={setRowSelection}
                  enableStickyHeader={true}
                  enableSortingRemoval={false}
                  renderRowActions={({ row }) => (
                    <RenderRowActions
                      selectedRowsData={selectedRowsData}
                      setSelectedRowsData={setSelectedRowsData}
                      row={row}
                      handleViewLoanDetailsActionRowClick={handleViewLoanDetailsActionRowClick}
                      openHistoricalFlyout={() => setOpenHistoricalFlyout(true)}
                      setRowSelection={setRowSelection}
                      setOpenAddRemarksFlyout={setOpenAddRemarksFlyout}
                      setOpenForceStatusFlyout={setOpenForceStatusFlyout}
                    />
                  )}
                  muiTableContainerProps={{
                    ref: tableContainerRef,
                    sx: { maxHeight: '600px', minHeight: '300px' },
                    onScroll: (event: UIEvent<HTMLDivElement>) =>
                      fetchMoreOnBottomReached(event.target as HTMLDivElement)
                  }}
                  manualSorting
                  onSortingChange={setSorting}
                  state={{
                    rowSelection,
                    isLoading: isLoading || isFetching,
                    sorting
                  }}
                />
              </div>
            }
            rightPane={
              <RightPaneContent
                subHeaderText={t('CMHC-Account-Numbers-Text')}
                bodyContent={t('Master-Pool-Right-Pane-Body-Content')}
                selectedRowSubHeaderData={selectedRowsData[0]?.cmhcAccountNumber.toString() || ''}
                noResultsBodyContent={t('Master-Pool-Right-Pane-Select-A-Row')}
                moreThenOneIsSelectedContent={t('More-Then-One-Master-Pool-Is-Selected')}
                noErrorsContent={t('Master-Pool-Has-No-Errors')}
                handleCloseFlyout={handleCloseFlyout}
                count={selectedRowsData.length}
                data={selectedRowsData}
              />
            }
          />
        </>
      )}
    </>
  );
};

export default memo(HbtMasterPoolLoansInventoryTable);
